import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

/**
 * Bottom tab bar — 6 side slots in a rounded pill bar with a CONVEX bump
 * at top centre. The bump + bar are a single SVG silhouette so the eye
 * reads them as one shape, not "FAB on top of bar". The Home tile is just
 * the brand house icon positioned over the bump (no separate circle).
 */
type SideTabDef = {
  to: string
  labelKey: 'tabs.tarot' | 'tabs.astro' | 'tabs.luna' | 'tabs.numbers' | 'tabs.compat' | 'tabs.profile'
} & ({ iconSrc: string } | { iconNode: React.ReactNode })

const sideTabs: SideTabDef[] = [
  { to: '/tarot',   iconSrc: '/icons/tarot.png',    labelKey: 'tabs.tarot' },
  { to: '/astro',   iconSrc: '/icons/natal.png',    labelKey: 'tabs.astro' },
  { to: '/luna',    iconSrc: '/icons/forecast.png', labelKey: 'tabs.luna' },
  { to: '/numbers', iconSrc: '/icons/numbers.png',  labelKey: 'tabs.numbers' },
  { to: '/compat',  iconSrc: '/icons/compat.png',   labelKey: 'tabs.compat' },
  { to: '/profile', iconSrc: '/icons/profile.png', labelKey: 'tabs.profile' },
]

// Visual constants
const BAR_HEIGHT = 60
const BUMP_WIDTH_HALF = 48      // half-width of bump base — wider gives a softer, more grounded bump
const BUMP_HEIGHT = 20          // bump height — controls how much the dome protrudes
const HOME_VERTICAL_OFFSET = 12  // pixels to push the home icon further into the bar
const HOME_ICON_PX = 46         // home.png is bigger than the bump — straddles the bump/bar seam
const SVG_VB_W = 384
const SVG_VB_H = BAR_HEIGHT + BUMP_HEIGHT

export function BottomTabs() {
  const { t } = useTranslation()
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 pointer-events-none">
      {/* Fade strip so content scrolling under the bar transitions to cream */}
      <div className="h-24 bg-gradient-to-t from-cream via-cream/95 to-cream/0" />
      <nav className="pointer-events-auto absolute inset-x-0 bottom-0 mx-auto max-w-md px-3 pb-3">
        {/* Wrapper has padding-top equal to BUMP_HEIGHT so absolutely
            positioned children (the SVG silhouette + side tabs + home
            icon) all align on the same baseline. */}
        <div className="relative" style={{ height: BAR_HEIGHT + BUMP_HEIGHT }}>
          <BumpedBarSvg />

          {/* Side tabs overlaid on the bar (excluding the bump area) */}
          <div
            className="absolute inset-x-0 grid grid-cols-7 items-stretch px-1"
            style={{ top: BUMP_HEIGHT, bottom: 0 }}
          >
            {sideTabs.slice(0, 3).map((tab) => (
              <SideTab key={tab.to} tab={tab} t={t} />
            ))}
            <div />
            {sideTabs.slice(3).map((tab) => (
              <SideTab key={tab.to} tab={tab} t={t} />
            ))}
          </div>

          {/* Home icon sits inside the bump — no extra circle, no ring on
              active. The bar's own silhouette IS the circle outline. */}
          <NavLink
            to="/"
            end
            aria-label={t('tabs.home')}
            className="absolute left-1/2 flex items-center justify-center transition active:scale-[0.92]"
            style={{
              width: HOME_ICON_PX,
              height: HOME_ICON_PX,
              // Place the icon centred on the bump tip (top of bump). Bigger
              // than the bump itself, so its lower portion drops into the
              // bar — reads as "house sitting on the bump" rather than
              // floating above it.
              top: -HOME_ICON_PX / 2 + BUMP_HEIGHT + HOME_VERTICAL_OFFSET,
              transform: 'translate(-50%, 0)',
            }}
          >
            <img
              src="/icons/home.png"
              alt=""
              aria-hidden="true"
              className="select-none"
              style={{ width: HOME_ICON_PX, height: HOME_ICON_PX }}
              draggable={false}
            />
          </NavLink>
        </div>
      </nav>
    </div>
  )
}

function SideTab({
  tab,
  t,
}: {
  tab: SideTabDef
  t: (key: string) => string
}) {
  return (
    <NavLink
      to={tab.to}
      className={({ isActive }) =>
        clsx(
          'flex flex-col items-center justify-center gap-1 px-0.5 text-[10px] leading-tight transition',
          isActive ? 'text-sage-900' : 'text-sage-600 hover:text-sage-800',
        )
      }
    >
      {'iconSrc' in tab ? (
        <img
          src={tab.iconSrc}
          alt=""
          aria-hidden="true"
          className="h-7 w-7 select-none"
          draggable={false}
        />
      ) : (
        <span className="flex h-7 w-7 items-center justify-center">{tab.iconNode}</span>
      )}
      <span className="block max-w-full truncate text-center">{t(tab.labelKey)}</span>
    </NavLink>
  )
}

/**
 * Rounded pill bar with a smooth convex bump at top centre. Single SVG
 * path — the bump is a cubic-Bezier bell shape so the tangent matches
 * the horizontal bar edge on both flanges (no 90° corners). Border on
 * the silhouette, fill is bar-cream.
 */
function BumpedBarSvg() {
  const W = SVG_VB_W
  const BH = BUMP_HEIGHT  // y-offset where bar's top edge actually sits
  const H = BAR_HEIGHT    // bar's own height (below the bump line)
  const R = 24            // corner radius of the bar
  const BW = BUMP_WIDTH_HALF // half-width of bump base
  const CX = W / 2
  const TOTAL_H = BH + H

  // Bezier control points for the bell-shape bump.
  // Left half: start at (CX-BW, BH) on the bar edge, end at (CX, 0) at bump tip.
  //   - C1 at (CX-BW/2, BH) keeps the start tangent horizontal → smooth merge
  //     with the bar.
  //   - C2 at (CX-BW/2, 0) keeps the end tangent horizontal → smooth round
  //     top at (CX, 0).
  // Right half mirrors.
  const C = BW / 2

  const path = [
    `M ${R} ${BH}`,                                  // start at top-left after corner
    `H ${CX - BW}`,                                  // run to bump's left flange
    `C ${CX - C} ${BH}, ${CX - C} 0, ${CX} 0`,       // left half of bell
    `C ${CX + C} 0, ${CX + C} ${BH}, ${CX + BW} ${BH}`, // right half of bell
    `H ${W - R}`,                                    // continue along bar top
    `A ${R} ${R} 0 0 1 ${W} ${BH + R}`,              // top-right corner
    `V ${TOTAL_H - R}`,                              // right edge
    `A ${R} ${R} 0 0 1 ${W - R} ${TOTAL_H}`,         // bottom-right corner
    `H ${R}`,                                        // bottom edge
    `A ${R} ${R} 0 0 1 0 ${TOTAL_H - R}`,            // bottom-left corner
    `V ${BH + R}`,                                   // left edge
    `A ${R} ${R} 0 0 1 ${R} ${BH}`,                  // top-left corner
    'Z',
  ].join(' ')
  return (
    <svg
      viewBox={`0 0 ${W} ${SVG_VB_H}`}
      preserveAspectRatio="none"
      className="absolute inset-0 block h-full w-full"
      style={{ filter: 'drop-shadow(0 4px 10px rgba(52, 68, 43, 0.12))' }}
      aria-hidden
    >
      <path
        d={path}
        fill="rgba(250, 246, 238, 0.97)"
        stroke="rgba(125, 154, 130, 0.45)"
        strokeWidth="1"
      />
    </svg>
  )
}
